// RiskMatrix.js
import React, { useState } from 'react';
import riskData from './riskdata/riskData';

function RiskMatrix() {
  // Cascading filter state variables
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedRiskId, setSelectedRiskId] = useState('');

  // Unique industry options from riskData (includes Common)
  const uniqueIndustries = [...new Set(riskData.map(item => item.industry))];

  // Filter riskData for Department options based on selected industry.
  // If an item is defined as "Common", it's applicable to every industry.
  const filteredForDept = riskData.filter(item =>
    selectedIndustry ? (item.industry === selectedIndustry || item.industry === 'Common') : true
  );
  const uniqueDepartments = [...new Set(filteredForDept.map(item => item.department))];

  // Filter riskData for Risk/Process options based on selected department (and industry if set).
  const filteredForRisk = filteredForDept.filter(item =>
    selectedDepartment ? (item.department === selectedDepartment || item.department === 'Common') : true
  );
  const uniqueRiskIds = [...new Set(filteredForRisk.map(item => item.riskId))];

  // Final filtering for display based on all cascading filters
  const finalFilteredData = riskData.filter(item => {
    const industryMatch = selectedIndustry ? (item.industry === selectedIndustry || item.industry === 'Common') : true;
    const departmentMatch = selectedDepartment ? (item.department === selectedDepartment || item.department === 'Common') : true;
    const riskMatch = selectedRiskId ? item.riskId === selectedRiskId : true;
    return industryMatch && departmentMatch && riskMatch;
  });

  // Reset lower-level filters when a higher-level filter changes
  const handleIndustryChange = (e) => {
    setSelectedIndustry(e.target.value);
    setSelectedDepartment('');
    setSelectedRiskId('');
  };

  const handleDepartmentChange = (e) => {
    setSelectedDepartment(e.target.value);
    setSelectedRiskId('');
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6 text-center">Risk and Control Matrix</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {/* Industry Filter */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">Industry</label>
          <select 
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedIndustry}
            onChange={handleIndustryChange}
          >
            <option value="">All Industries</option>
            {uniqueIndustries.map((ind, index) => (
              <option key={index} value={ind}>{ind}</option>
            ))}
          </select>
        </div>
        {/* Department Filter */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">Department</label>
          <select 
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedDepartment}
            onChange={handleDepartmentChange}
          >
            <option value="">All Departments</option>
            {uniqueDepartments.map((dept, index) => (
              <option key={index} value={dept}>{dept}</option>
            ))}
          </select>
        </div>
        {/* Risk / Process Filter */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">Risk / Process</label>
          <select 
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedRiskId}
            onChange={(e) => setSelectedRiskId(e.target.value)}
          >
            <option value="">All Risks</option>
            {uniqueRiskIds.map((rid, index) => (
              <option key={index} value={rid}>{rid}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6">
        {finalFilteredData.length > 0 ? (
          finalFilteredData.map((item, index) => (
            <div 
              key={index} 
              className="border border-gray-200 rounded-lg p-6 shadow hover:shadow-xl transition-shadow duration-300"
            >
              <h2 className="text-2xl font-semibold mb-3">
                {item.industry} - {item.department} - {item.riskId}
              </h2>
              <p className="mb-2">
                <span className="font-bold">Risk Description:</span> {item.riskDescription}
              </p>
              <p className="mb-2">
                <span className="font-bold">Risk Cause:</span> {item.riskCause.join(', ')}
              </p>
              <p className="mb-2">
                <span className="font-bold">Consequence:</span> {item.consequence}
              </p>
              <p className="mb-2">
                <span className="font-bold">Risk Category:</span> {item.riskCategory}
              </p>
              <p className="mb-2">
                <span className="font-bold">Control ID:</span> {item.processControlId}
              </p>
              <p className="mb-2">
                <span className="font-bold">Control Description:</span> {item.processControlDescription}
              </p>
              <p className="mb-2">
                <span className="font-bold">Control Design:</span> {item.processControlDesign}
              </p>
              <p className="mb-2">
                <span className="font-bold">Type of Control:</span> {item.typeOfProcessControl}
              </p>
              <p className="mb-2">
                <span className="font-bold">Control Category:</span> {item.processControlCategory}
              </p>
              <p className="mb-2">
                <span className="font-bold">Frequency:</span> {item.frequency}
              </p>
              <p className="mb-2">
                <span className="font-bold">Owner:</span> {item.processControlOwner}
              </p>
              <p className="mb-2">
                <span className="font-bold">Expected Deviations:</span> {item.expectedDeviations.join('; ')}
              </p>
              <p className="mb-2">
                <span className="font-bold">Audit Testing Procedures:</span> {item.auditTestingProcedures.join('; ')}
              </p>
            </div>
          ))
        ) : (
          <p className="text-gray-600 text-center">No records found for the selected filters.</p>
        )}
      </div>
    </div>
  );
}

export default RiskMatrix;
